import React, { useState, useEffect } from 'react';
import { Settings, LogOut, User, ChevronDown, Users, Menu, Heart, Check, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../lib/supabase';
import { WeddingInvitation } from '../types/invitation';
import UserProfileModal from './UserProfileModal';
import CollaboratorModal from './CollaboratorModal';
import CollaborationRoleBadge from './CollaborationRoleBadge';
import { createLogger } from '../lib/logger';

const log = createLogger('app');

interface HeaderProps {
  activeModule: string;
  onToggleSidebar: () => void;
  // 新增邀請函選擇器相關 props
  weddingInvitations?: WeddingInvitation[];
  activeInvitationId?: string | null;
  onInvitationSelect?: (invitationId: string, index: number) => void;
}

export const Header: React.FC<HeaderProps> = ({
  activeModule,
  onToggleSidebar,
  weddingInvitations = [],
  activeInvitationId,
  onInvitationSelect
}) => {
  const getModuleTitle = () => {
    switch (activeModule) {
      case 'guests':
        return 'Guest Management';
      case 'rsvp':
        return '賓客管理';
      case 'website':
        return '婚禮網站';
      case 'budget':
        return '預算管理';
      case 'tables':
        return '座位安排';
      case 'gifts':
        return '紅包管理';
      case 'timeline':
        return '時間規劃';
      case 'invitations':
        return '邀請管理';
      default:
        return '總覽';
    }
  };
  return <header className="h-16 bg-wedding-bg border-b border-wedding-pink/30 py-4 px-6 flex items-center justify-between">
      <div className="flex items-center">
        <button onClick={onToggleSidebar} className="p-2 rounded-full hover:bg-wedding-pink/10 mr-4">
          <Menu className="h-6 w-6 text-wedding-text-title" />
        </button>
        <h1 className="text-xl font-semibold text-wedding-text-title">{getModuleTitle()}</h1>
      </div>
      <div className="flex items-center justify-end flex-1 min-w-0">
        <UserMenu 
          activeInvitationId={activeInvitationId}
          weddingInvitations={weddingInvitations}
          onInvitationSelect={onInvitationSelect}
        />
      </div>
    </header>;
};

// 用戶選單組件 - 現在接收邀請函相關 props 和切換回調
const UserMenu: React.FC<{ 
  activeInvitationId?: string | null;
  weddingInvitations?: WeddingInvitation[];
  onInvitationSelect?: (invitationId: string, index: number) => void;
}> = ({ activeInvitationId, weddingInvitations = [], onInvitationSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCollaboratorModal, setShowCollaboratorModal] = useState(false);
  const [userAuthMethods, setUserAuthMethods] = useState<string[]>([]);
  const { user, userProfile, signOut } = useAuth();

  // 檢測用戶認證方式
  useEffect(() => {
    const checkAuthMethods = async () => {
      if (user) {
        try {
          const { methods } = await authService.getUserAuthMethods();
          setUserAuthMethods(methods);
        } catch (error) {
          log.error('檢查認證方式失敗:', error);
        }
      }
    };
    checkAuthMethods();
  }, [user]);
  
  // 使用傳入的 activeInvitationId 或找到第一個用戶擁有的邀請函
  const currentInvitationId = activeInvitationId || 
    weddingInvitations.find(inv => inv.role === 'owner')?.id || 
    weddingInvitations[0]?.id || 
    null;

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
    } catch (error) {
      log.error('登出失敗:', error);
    }
  };

  const displayName = userProfile?.displayName || user?.email?.split('@')[0] || '用戶';
  const initials = displayName.length >= 2 ? displayName.substring(0, 2).toUpperCase() : 'U';
  
  // 邀請函切換邏輯
  const hasMultipleInvitations = weddingInvitations.length > 1;
  const shouldShowInvitationSelect = hasMultipleInvitations && onInvitationSelect;
  
  const handleInvitationSelect = (invitation: WeddingInvitation) => {
    if (onInvitationSelect) {
      const index = weddingInvitations.findIndex(inv => inv.id === invitation.id);
      onInvitationSelect(invitation.id, index);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-wedding-pink/10 transition-colors"
      >
        <div className="h-8 w-8 rounded-full bg-wedding-pink flex items-center justify-center text-white font-medium">
          {initials}
        </div>
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-wedding-text-title">{displayName}</p>
          <p className="text-xs text-wedding-text-body">{user?.email}</p>
        </div>
        <ChevronDown className="h-4 w-4 text-wedding-text-body" />
      </button>

      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* 下拉選單 */}
          <div className="absolute right-0 top-full mt-2 w-72 bg-wedding-bg rounded-lg shadow-lg border border-wedding-pink/30 z-20">
            <div className="p-3 border-b border-wedding-pink/30">
              <p className="font-medium text-wedding-text-title">{displayName}</p>
              <p className="text-sm text-wedding-text-body">{user?.email}</p>
            </div>
            
            {/* 邀請函選擇區域 - 只在多個邀請函時顯示 */}
            {shouldShowInvitationSelect && (
              <div className="border-b border-wedding-pink/30">
                <div className="p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Heart className="h-4 w-4 text-wedding-pink" />
                    <span className="text-sm font-medium text-wedding-text-title">邀請函切換</span>
                  </div>
                  <div className="space-y-2">
                    {weddingInvitations.map((invitation) => (
                      <button
                        key={invitation.id}
                        onClick={() => handleInvitationSelect(invitation)}
                        className={`w-full flex items-center justify-between p-2 rounded-md text-left transition-colors ${
                          invitation.id === activeInvitationId 
                            ? 'bg-wedding-pink/10 border border-wedding-pink/20' 
                            : 'hover:bg-wedding-pink/5'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-wedding-text-title truncate">
                              {invitation.brideName} & {invitation.groomName}
                            </span>
                            {invitation.role && (
                              <CollaborationRoleBadge role={invitation.role} size="small" />
                            )}
                          </div>
                          <div className="text-xs text-wedding-text-body truncate mt-0.5">
                            {invitation.weddingDate} • {invitation.venue}
                          </div>
                        </div>
                        {invitation.id === activeInvitationId && (
                          <Check className="h-4 w-4 text-wedding-pink flex-shrink-0 ml-2" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <div className="py-2">
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowProfileModal(true);
                }}
                className="w-full flex items-center px-3 py-2 text-sm hover:bg-wedding-pink/10 transition-colors text-wedding-text-body"
              >
                <User className="h-4 w-4 mr-3" />
                個人資料
              </button>
              
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowCollaboratorModal(true);
                }}
                disabled={!currentInvitationId}
                className="w-full flex items-center px-3 py-2 text-sm hover:bg-wedding-pink/10 transition-colors text-wedding-text-body disabled:opacity-50 disabled:cursor-not-allowed"
                title={!currentInvitationId ? '請先建立邀請函' : ''}
              >
                <Users className="h-4 w-4 mr-3" />
                協作者管理
              </button>
              
              {/* 設定密碼選項 - 只對 OAuth 用戶且未設定密碼時顯示 */}
              {userAuthMethods.includes('google') && !userAuthMethods.includes('password') && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    window.location.href = '/set-password';
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-blue-600"
                >
                  <Plus className="h-4 w-4 mr-3" />
                  設定登入密碼
                </button>
              )}
              
              <button
                onClick={() => {
                  setIsOpen(false);
                  // TODO: 打開設定頁面
                }}
                className="w-full flex items-center px-3 py-2 text-sm hover:bg-wedding-pink/10 transition-colors text-wedding-text-body"
              >
                <Settings className="h-4 w-4 mr-3" />
                設定
              </button>
            </div>
            
            <div className="border-t border-wedding-pink/30 py-2">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-3" />
                登出
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* 個人資料 Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
      
      {/* 協作者管理 Modal */}
      {currentInvitationId && (
        <CollaboratorModal
          isOpen={showCollaboratorModal}
          onClose={() => setShowCollaboratorModal(false)}
          invitationId={currentInvitationId}
        />
      )}
    </div>
  );
};